/**
 * @flow
 */

import Config from 'config';
import qs from 'querystring';
import uuid from 'uuid';
import { debounce } from 'underscore';

import { getParams, isMobile } from './util';


export type WidgetInitState = {
  targetType: string;
  targetId: string;
  widgetHeight?: string;
  skipQueue?: boolean;
  skipPresence?: boolean;
  widget?: string;
  filtersEnabled?: boolean;
  q?: string;
  ts?: string;
  qsession?: string;
  lang?: string;
};

type WidgetConfig = WidgetInitState & {
  widgetId: string;
};

export type WidgetParams = {
  state: string,
  utm_campaign?: string,
  utm_medium?: string,
  utm_source?: string,
  utm_term?: string,
  utm_name?: string
};

const MOBILE_SIZE = 768;

class Widget {
  _container: Element;
  _iframe: HTMLIFrameElement;
  _id: string;
  _config: WidgetConfig;

  constructor(container: Element, initState: WidgetInitState) {
    this._id = uuid();
    this._container = container;
    this._config = { ...initState, widgetId: this._id };

    const params: string = qs.stringify(getParams(window.location.search, this._config));
    this._iframe = this.buildIFrame(this.buildURL(initState.targetId, initState.lang, params));
  }

  id(): string {
    return this._id;
  }

  window(): any {
    return this._iframe.contentWindow;
  }

  reload(newConfig: Object): void {
    this._config = { ...this._config, ...newConfig };
    const { targetId, lang } = this._config;
    const params: string = qs.stringify(getParams(window.location.search, this._config));
    const url: string = this.buildURL(targetId, lang, params);
    this._iframe.src = url;
  }

  /* To be overwritten */
  buildIFrame(url: string): HTMLIFrameElement {
    const iframe: HTMLIFrameElement = document.createElement('iframe');
    iframe.src = url;
    return iframe;
  }

  /* To be overwritten */
  buildURL(targetId: string, lang: string, params: string): string {
    return '';
  }
}

// add show, hide functionality to widget as well as a fullscreen iframe
class PopupWidget extends Widget {
  _hideAll: HTMLStyleElement;

  constructor(initState: WidgetInitState) {
    super((document: any).body, initState);

    this._hideAll = document.createElement('style');
    this._hideAll.innerHTML = 'body > *:not(.tmjs-iframe) { display: none !important; }';
  }

  isAttached(): boolean {
    return this._iframe.style.display !== 'none';
  }

  // create IFrame in container and mount the widget
  attach(): void {
    if (this.isAttached()) {
      return;
    }

    this._iframe.style.display = 'block';

    if (isMobile(navigator.userAgent)) {
      this._iframe.style.position = 'relative';
      this._iframe.style.height = `${window.innerHeight}px`;
      this._container.appendChild(this._hideAll);
    }

    (document: any).body.style.cssText = 'overflow: hidden; height: auto';
    this._container.appendChild(this._iframe);
  }

  detach(): void {
    if (!this.isAttached()) {
      return;
    }

    if (isMobile(navigator.userAgent)) {
      this._container.removeChild(this._hideAll);
    }

    this._iframe.style.display = 'none';

    (document: any).body.style.cssText = 'overflow: auto; overflow: initial'; // IE11 doesn't support overflow "initial", fallback to "auto"
    this._container.removeChild(this._iframe);
  }

  buildIFrame(url: string): HTMLIFrameElement {
    const iframe: HTMLIFrameElement = document.createElement('iframe');
    iframe.src = url;
    iframe.id = `universe-${this._id}`;

    // $FlowIssue Non-standard DOM property
    iframe.allowFullscreen = true;
    iframe.setAttribute('allowpaymentrequest', '');
    iframe.setAttribute('allow', 'payment *');
    // $FlowIssue Legacy DOM property
    iframe.allowTransparency = true;
    iframe.className = 'tmjs-iframe';

    iframe.style.position = 'fixed';
    iframe.style.top = '0px';
    iframe.style.left = '0px';
    iframe.style.display = 'none';
    iframe.style.margin = '0';
    iframe.style.padding = '0';
    iframe.style.border = 'none';
    iframe.style.height = '100vh';
    iframe.style.width = '100vw';
    iframe.style.zIndex = '2147483647';
    return iframe;
  }
}

export class EmbedWidget extends Widget {
  mount(target: HTMLElement): void {
    this._container.insertBefore(this._iframe, target);
  }

  destroy(): void {
    this._container.removeChild(this._iframe);
  }

  resize(height: string): void {
    if (!this._config.widgetHeight) {
      this._iframe.style.height = height;
    }
  }

  buildIFrame(url: string): HTMLIFrameElement {
    const iframe: HTMLIFrameElement = document.createElement('iframe');
    iframe.src = url;
    iframe.id = `universe-${this._id}`;
    iframe.setAttribute('ref', 'tmjsIframe');
    iframe.className = 'tmjs-iframe';
    iframe.style.overflow = 'hidden';
    iframe.style.margin = '0';
    iframe.style.padding = '0';
    iframe.style.border = 'none';

    iframe.style.width = '100%';
    iframe.style.height = this._config.widgetHeight || '0px';

    return iframe;
  }
}

export class CheckoutWidget extends PopupWidget {
  buildURL(targetId: string, lang: string, params: string): string {
    const langParam = lang ? `&lang=${lang}` : '';
    // Forward the Fastly queue-it session token top-level so the checkout
    // middleware can validate it instead of relying on the forgeable skipQueue.
    const qsessionParam = this._config.qsession ? `&qsession=${encodeURIComponent(this._config.qsession)}` : '';

    if (__EMBED_VIA_CHECKOUT__) {
      return `${Config.betaCheckout}/${targetId}?${params}${langParam}${qsessionParam}`;
    }

    return `${Config.checkout}/${targetId}?redirectTarget=events${langParam}&${params}${qsessionParam}`;
  }
}

export class CalendarWidget extends EmbedWidget {
  mount(target: HTMLElement): void {
    this._container.insertBefore(this._iframe, target);
    window.addEventListener('resize', this._toggleSize);
  }

  destroy(): void {
    this._container.removeChild(this._iframe);
    window.removeEventListener('resize', this._toggleSize);
  }

  _toggleSize = (): void => {
    debounce(this.toggleSize(this._iframe), 50);
  }

  buildIFrame(url: string): HTMLIFrameElement {
    const iframe: HTMLIFrameElement = document.createElement('iframe');
    iframe.src = url;
    iframe.id = `universe-${this._id}`;
    iframe.setAttribute('ref', 'tmjsIframe');
    iframe.className = 'tmjs-iframe';
    iframe.style.border = 'none';
    iframe.style.backgroundColor = 'white';
    iframe.style.boxShadow = '0 0 8px 1px rgba(28, 35, 43, 0.15)';
    iframe.style.margin = 'unset';
    iframe.style.borderRadius = '6px';
    iframe.title = 'Event Dates Calendar';

    if (document.location.href.match(new RegExp(`${Config.api.host}\/calendars`))) {
      iframe.style.boxShadow = 'unset';
      iframe.style.margin = 'unset';
    }

    this.toggleSize(iframe);

    if (this._container.clientWidth < MOBILE_SIZE) {
      iframe.style.height = '575px';
    }
    return iframe;
  }

  toggleSize(iframe: HTMLIFrameElement): void {
    if (this._container.clientWidth < MOBILE_SIZE) {
      iframe.style.width = '100%';
      iframe.style.height = '602px';
      iframe.style.minWidth = '0';
    }
    else {
      iframe.style.width = '100%';
      iframe.style.height = this._config.filtersEnabled ? '605px' : '524px';
      iframe.style.maxWidth = '888px';
    }
  }

  buildURL(targetId: string, lang: string, params: string): string {
    const langParam = lang ? `&lang=${lang}` : '';

    if (__EMBED_VIA_CHECKOUT__) {
      return `${Config.betaCalendar}/${targetId}?${params}${langParam}`;
    }

    return `${Config.calendar}/${targetId}?redirectTarget=calendar${langParam}&${params}`;
  }
}

export class TicketWidget extends EmbedWidget {
  buildURL(targetId: string, lang: string, params: string): string {
    const langParam = lang ? `&lang=${lang}` : '';

    if (__EMBED_VIA_CHECKOUT__) {
      return `${Config.betaCheckout}/tickets/${targetId}?${params}${langParam}`;
    }

    return `${Config.tickets}/${targetId}?redirectTarget=tickets${langParam}&${params}`;
  }
}

export class ListingsWidget extends EmbedWidget {
  buildURL(targetId: string, lang: string, params: string): string {
    const langParam = lang ? `&lang=${lang}` : '';

    if (__EMBED_VIA_CHECKOUT__) {
      return `${Config.betaCheckout}/listings/${targetId}?${params}${langParam}`;
    }

    return `${Config.listings}/${targetId}?redirectTarget=listings${langParam}&${params}`;
  }
}


export type UniversePopupWidget = CheckoutWidget;

export type UniverseEmbedWidget = CalendarWidget | ListingsWidget | TicketWidget;



// WEBPACK FOOTER //
// ./src/bootstrap/widget.js